import { _ as _defineProperty, g as getValueByPath } from './helpers-d639255d.js';
import { B as BaseComponentMixin, c as config, n as normalizeComponent } from './plugins-4295ce0e.js';
import { _ as __vue_component__$1 } from './Icon-9c764942.js';
import { F as FormElementMixin } from './FormElementMixin-f9847bcc.js';

/**
 * Get user Input. Use with Field to access all functionalities
 * @displayName Input
 * @example ./examples/Input.md
 * @style _input.scss
 */

var script = {
  name: 'OInput',
  components: _defineProperty({}, __vue_component__$1.name, __vue_component__$1),
  mixins: [BaseComponentMixin, FormElementMixin],
  inheritAttrs: false,
  provide: function provide() {
    return {
      $elementRef: this.type === 'textarea' ? 'textarea' : 'input'
    };
  },
  props: {
    /**
     * @model
     */
    value: [Number, String],

    /**
     * Input type, like native
     * @values Any native input type, and textarea
     */
    type: {
      type: String,
      default: 'text'
    },

    /**
     * Vertical size of input, optional
     * @values small, medium, large
     */
    size: String,

    /**
     * 	Adds the reveal password functionality
     */
    passwordReveal: Boolean,

    /**
     * Makes the icon clickable
     */
    iconClickable: Boolean,

    /**
     * Show character counter when maxlength prop is passed
     */
    hasCounter: {
      type: Boolean,
      default: function _default() {
        return getValueByPath(config, 'input.counter', false);
      }
    },

    /**
     * 	Icon name to be added on the right side
     */
    iconRight: String,

    /**
     * Make the icon right clickable
     */
    iconRightClickable: Boolean,
    rootClass: String,
    controlExpandedClass: String,
    controlIconLeftClass: String,
    controlIconRightClass: String,
    inputClass: String,
    roundedClass: String,
    iconLeftClass: String,
    iconRightClass: String,
    counterClass: String,
    counterInvisibleClass: String,
    sizeClass: String,
    variantClass: String
  },
  data: function data() {
    return {
      newValue: this.value,
      newType: this.type,
      newAutocomplete: this.autocomplete || getValueByPath(config, 'input.autocompletete', 'off'),
      isPasswordVisible: false
    };
  },
  computed: {
    rootClasses: function rootClasses() {
      return [this.computedClass('input', 'rootClass', 'o-control-input'), _defineProperty({}, this.computedClass('input', 'controlExpandedClass', 'o-control-input-expanded'), this.expanded), _defineProperty({}, this.computedClass('input', 'controlIconLeftClass', 'o-control-input-icons-left'), this.icon), _defineProperty({}, this.computedClass('input', 'controlIconRightClass', 'o-control-input-icons-right'), this.hasIconRight)];
    },
    inputClasses: function inputClasses() {
      return [this.computedClass('input', 'inputClass', 'o-input'), _defineProperty({}, this.computedClass('input', 'roundedClass', 'o-input-rounded'), this.rounded), _defineProperty({}, "".concat(this.computedClass('input', 'sizeClass', 'o-size-')).concat(this.size), this.size), _defineProperty({}, "".concat(this.computedClass('input', 'variantClass', 'o-color-')).concat(this.statusVariant), this.statusVariant)];
    },
    iconLeftClasses: function iconLeftClasses() {
      return [this.computedClass('input', 'iconLeftClass', 'o-icon-left')];
    },
    iconRightClasses: function iconRightClasses() {
      return [this.computedClass('input', 'iconRightClass', 'o-icon-right')];
    },
    counterClasses: function counterClasses() {
      return [this.computedClass('input', 'counterClass', 'o-input-counter'), _defineProperty({}, this.computedClass('input', 'counterInvisibleClass', 'o-input-counter-invisible'), !this.isFocused)];
    },
    computedValue: {
      get: function get() {
        return this.newValue;
      },
      set: function set(value) {
        this.newValue = value;
        this.$emit('input', value);
        !this.isValid && this.checkHtml5Validity();
      }
    },
    hasIconRight: function hasIconRight() {
      return this.passwordReveal || this.statusIcon && this.statusVariantIcon || this.iconRight;
    },
    rightIcon: function rightIcon() {
      if (this.passwordReveal) {
        return this.passwordVisibleIcon;
      } else if (this.iconRight) {
        return this.iconRight;
      }

      return this.statusVariantIcon;
    },
    rightIconVariant: function rightIconVariant() {
      if (this.passwordReveal || this.iconRight) {
        return;
      }

      return this.statusVariant;
    },

    /**
    * Icon name (MDI) based on the type.
    */
    statusVariantIcon: function statusVariantIcon() {
      switch (this.statusVariant) {
        case 'success':
          return 'check';

        case 'danger':
          return 'alert-circle';

        case 'info':
          return 'information';

        case 'warning':
          return 'alert';

        default:
          return '';
      }
    },

    /**
    * Check if have any message prop from parent if it's a Field.
    */
    hasMessage: function hasMessage() {
      return !!this.statusMessage;
    },

    /**
    * Current password-reveal icon name.
    */
    passwordVisibleIcon: function passwordVisibleIcon() {
      return !this.isPasswordVisible ? 'eye' : 'eye-off';
    },

    /**
    * Get value length
    */
    valueLength: function valueLength() {
      if (typeof this.computedValue === 'string') {
        return this.computedValue.length;
      } else if (typeof this.computedValue === 'number') {
        return this.computedValue.toString().length;
      }

      return 0;
    }
  },
  watch: {
    /**
    * When v-model is changed:
    *   1. Set internal value.
    */
    value: function value(_value) {
      this.newValue = _value;
    }
  },
  methods: {
    /**
    * Toggle the visibility of a password-reveal input
    * by changing the type and focus the input right away.
    */
    togglePasswordVisibility: function togglePasswordVisibility() {
      var _this = this;

      this.isPasswordVisible = !this.isPasswordVisible;
      this.newType = this.isPasswordVisible ? 'text' : 'password';
      this.$nextTick(function () {
        _this.focus();
      });
    },
    iconClick: function iconClick(emit, event) {
      var _this2 = this;

      this.$emit(emit, event);
      this.$nextTick(function () {
        _this2.focus();
      });
    },
    rightIconClick: function rightIconClick(event) {
      if (this.passwordReveal) {
        this.togglePasswordVisibility();
      } else if (this.iconRightClickable) {
        this.iconClick('icon-right-click', event);
      }
    }
  }
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { class: _vm.rootClasses },
    [
      _vm.newType === "checkbox" && _vm.type !== "textarea"
        ? _c(
            "input",
            _vm._b(
              {
                directives: [
                  {
                    name: "model",
                    rawName: "v-model",
                    value: _vm.computedValue,
                    expression: "computedValue"
                  }
                ],
                ref: "input",
                class: _vm.inputClasses,
                attrs: {
                  autocomplete: _vm.newAutocomplete,
                  maxlength: _vm.maxlength,
                  type: "checkbox"
                },
                domProps: {
                  checked: Array.isArray(_vm.computedValue)
                    ? _vm._i(_vm.computedValue, null) > -1
                    : _vm.computedValue
                },
                on: {
                  blur: _vm.onBlur,
                  focus: _vm.onFocus,
                  change: function($event) {
                    var $$a = _vm.computedValue,
                      $$el = $event.target,
                      $$c = $$el.checked ? true : false;
                    if (Array.isArray($$a)) {
                      var $$v = null,
                        $$i = _vm._i($$a, $$v);
                      if ($$el.checked) {
                        $$i < 0 && (_vm.computedValue = $$a.concat([$$v]));
                      } else {
                        $$i > -1 &&
                          (_vm.computedValue = $$a
                            .slice(0, $$i)
                            .concat($$a.slice($$i + 1)));
                      }
                    } else {
                      _vm.computedValue = $$c;
                    }
                  }
                }
              },
              "input",
              _vm.$attrs,
              false
            )
          )
        : _vm.newType === "radio" && _vm.type !== "textarea"
        ? _c(
            "input",
            _vm._b(
              {
                directives: [
                  {
                    name: "model",
                    rawName: "v-model",
                    value: _vm.computedValue,
                    expression: "computedValue"
                  }
                ],
                ref: "input",
                class: _vm.inputClasses,
                attrs: {
                  autocomplete: _vm.newAutocomplete,
                  maxlength: _vm.maxlength,
                  type: "radio"
                },
                domProps: { checked: _vm._q(_vm.computedValue, null) },
                on: {
                  blur: _vm.onBlur,
                  focus: _vm.onFocus,
                  change: function($event) {
                    _vm.computedValue = null;
                  }
                }
              },
              "input",
              _vm.$attrs,
              false
            )
          )
        : _vm.type !== "textarea"
        ? _c(
            "input",
            _vm._b(
              {
                directives: [
                  {
                    name: "model",
                    rawName: "v-model",
                    value: _vm.computedValue,
                    expression: "computedValue"
                  }
                ],
                ref: "input",
                class: _vm.inputClasses,
                attrs: {
                  autocomplete: _vm.newAutocomplete,
                  maxlength: _vm.maxlength,
                  type: _vm.newType
                },
                domProps: { value: _vm.computedValue },
                on: {
                  blur: _vm.onBlur,
                  focus: _vm.onFocus,
                  input: function($event) {
                    if ($event.target.composing) {
                      return
                    }
                    _vm.computedValue = $event.target.value;
                  }
                }
              },
              "input",
              _vm.$attrs,
              false
            )
          )
        : _c(
            "textarea",
            _vm._b(
              {
                directives: [
                  {
                    name: "model",
                    rawName: "v-model",
                    value: _vm.computedValue,
                    expression: "computedValue"
                  }
                ],
                ref: "textarea",
                class: _vm.inputClasses,
                attrs: { maxlength: _vm.maxlength },
                domProps: { value: _vm.computedValue },
                on: {
                  blur: _vm.onBlur,
                  focus: _vm.onFocus,
                  input: function($event) {
                    if ($event.target.composing) {
                      return
                    }
                    _vm.computedValue = $event.target.value;
                  }
                }
              },
              "textarea",
              _vm.$attrs,
              false
            )
          ),
      _vm._v(" "),
      _vm.icon
        ? _c("o-icon", {
            class: _vm.iconLeftClasses,
            attrs: {
              clickable: _vm.iconClickable,
              icon: _vm.icon,
              pack: _vm.iconPack,
              size: _vm.size
            },
            nativeOn: {
              click: function($event) {
                return _vm.iconClick("icon-click", $event)
              }
            }
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.hasIconRight
        ? _c("o-icon", {
            class: _vm.iconRightClasses,
            attrs: {
              clickable: _vm.passwordReveal || _vm.iconRightClickable,
              icon: _vm.rightIcon,
              pack: _vm.iconPack,
              size: _vm.size,
              variant: _vm.rightIconVariant,
              both: ""
            },
            nativeOn: {
              click: function($event) {
                return _vm.rightIconClick($event)
              }
            }
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.maxlength && _vm.hasCounter && _vm.type !== "number"
        ? _c("small", { class: _vm.counterClasses }, [
            _vm._v(
              "\n        " +
                _vm._s(_vm.valueLength) +
                " / " +
                _vm._s(_vm.maxlength) +
                "\n    "
            )
          ])
        : _vm._e()
    ],
    1
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

export { __vue_component__ as _ };
